import { useFetcher } from '@remix-run/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { loader } from '~/routes/_app._pm.app.project.$id.design-boards.$boardId';
import { useLoaderData } from '@remix-run/react';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';

import { UPDATE_BUILD_HUB_INSPIRATION_ITEM_VOTING_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-item-voting';
import type {
    SaveReactionMutation,
    RemoveReactionMutation,
    BuildHubRoleEnum,
} from '../../../../generated/graphql';

import { ReactionTypeEnum } from '../../../../generated/graphql';
import type {
    GroupedReactions,
    ReactionVoter,
} from '../data-mappers/map-inspiration-reactions';
import { useBuildHubData } from './use-build-hub-data';

import { useDebounceFn } from 'ahooks';

interface UseBuildHubBoardItemVotingProps {
    targetId: number;
    onSuccess?: (data: SaveReactionMutation | RemoveReactionMutation) => void;
}

interface UseBuildHubBoardItemVotingResult {
    reaction: GroupedReactions | undefined;
    handleUpVote: () => Promise<void>;
    handleDownVote: () => Promise<void>;
    handleRevokeVote: (reaction: ReactionTypeEnum) => Promise<void>;
    isVoting: boolean;
    upVoteCollaborators: ReactionVoter[];
    downVoteCollaborators: ReactionVoter[];
    isVotedUp: boolean;
    isVotedDown: boolean;
}

export const useBuildHubBoardItemVoting = (
    props: UseBuildHubBoardItemVotingProps
): UseBuildHubBoardItemVotingResult => {
    const { targetId, onSuccess } = props;
    const { reactions } = useLoaderData<typeof loader>();
    const { buildHub } = useBuildHubData();

    const collaborators = buildHub.collaborators;

    const { user } = useUserLoggedIn();
    if (!user || user.__typename === 'Guest') {
        throw new Error('User not found');
    }

    const matchedReaction = reactions.find(
        (reaction) => reaction.id === Number(targetId)
    );

    const fetcher = useFetcher<SaveReactionMutation | RemoveReactionMutation>();
    const onSuccessRef = useRef(onSuccess);

    // Optimistic state for voting
    const [optimisticVote, setOptimisticVote] = useState<{
        isVotedUp: boolean;
        isVotedDown: boolean;
        upVoteCollaborators: ReactionVoter[];
        downVoteCollaborators: ReactionVoter[];
    } | null>(null);

    // Update the ref when onSuccess changes
    useEffect(() => {
        onSuccessRef.current = onSuccess;
    }, [onSuccess]);

    // Debounced API call function
    const debouncedApiCall = useDebounceFn(
        (params: {
            targetId: number;
            reaction: ReactionTypeEnum;
            intent: string;
        }) => {
            fetcher.submit(params, {
                method: 'post',
                action: UPDATE_BUILD_HUB_INSPIRATION_ITEM_VOTING_API_ROUTE,
            });
        },
        { wait: 1000 }
    );

    const handleUpVote = useCallback(async () => {
        // Get current collaborators from server state
        const reaction = matchedReaction?.reactions;
        const serverUpVoteCollaborators = reaction?.up_vote ?? [];
        const serverDownVoteCollaborators = reaction?.down_vote ?? [];

        // Create optimistic user voter
        const optimisticUserVoter = getUserVoter(
            user,
            collaborators,
            buildHub.ownerId
        );

        const optimisticUpVoteCollaborators =
            optimisticVote?.upVoteCollaborators ?? serverUpVoteCollaborators;

        const optimisticDownVoteCollaborators =
            optimisticVote?.downVoteCollaborators ??
            serverDownVoteCollaborators;

        if (
            optimisticUpVoteCollaborators.some(
                (voter) => voter.id === optimisticUserVoter.id
            )
        ) {
            return;
        } else {
            optimisticUpVoteCollaborators.push(optimisticUserVoter);
        }

        // Optimistic update: immediately show as voted up
        setOptimisticVote({
            isVotedUp: true,
            isVotedDown: false,
            upVoteCollaborators: optimisticUpVoteCollaborators,
            downVoteCollaborators: optimisticDownVoteCollaborators.filter(
                (voter) => voter.id !== user.ID
            ),
        });

        // Use debounced API call
        debouncedApiCall.run({
            targetId,
            reaction: ReactionTypeEnum.UpVote,
            intent: 'save',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId, user, matchedReaction, collaborators, debouncedApiCall]);

    const handleDownVote = useCallback(async () => {
        // Get current collaborators from server state
        const reaction = matchedReaction?.reactions;
        const currentUpVoteCollaborators = reaction?.up_vote ?? [];
        const currentDownVoteCollaborators = reaction?.down_vote ?? [];

        // Create optimistic user voter
        const userVoter = getUserVoter(user, collaborators, buildHub.ownerId);

        // Optimistic update: immediately show as voted down
        setOptimisticVote({
            isVotedUp: false,
            isVotedDown: true,
            upVoteCollaborators: currentUpVoteCollaborators.filter(
                (voter) => voter.id !== user.ID
            ),
            downVoteCollaborators: [...currentDownVoteCollaborators, userVoter],
        });

        // Use debounced API call
        debouncedApiCall.run({
            targetId,
            reaction: ReactionTypeEnum.DownVote,
            intent: 'save',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId, user, matchedReaction, collaborators, debouncedApiCall]);

    const handleRevokeVote = useCallback(
        async (reaction: ReactionTypeEnum) => {
            if (!user) return;

            // Get current collaborators from server state
            const reactionData = matchedReaction?.reactions;
            const currentUpVoteCollaborators = reactionData?.up_vote ?? [];
            const currentDownVoteCollaborators = reactionData?.down_vote ?? [];

            // Optimistic update: immediately show as not voted
            setOptimisticVote({
                isVotedUp: false,
                isVotedDown: false,
                upVoteCollaborators: currentUpVoteCollaborators.filter(
                    (voter) => voter.id !== user.ID
                ),
                downVoteCollaborators: currentDownVoteCollaborators.filter(
                    (voter) => voter.id !== user.ID
                ),
            });

            // Use debounced API call
            debouncedApiCall.run({
                targetId,
                reaction,
                intent: 'remove',
            });
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [targetId, user, matchedReaction, collaborators, debouncedApiCall]
    );

    const reaction = matchedReaction?.reactions;
    const serverUpVoteCollaborators = reaction?.up_vote ?? [];
    const serverDownVoteCollaborators = reaction?.down_vote ?? [];

    // Use optimistic state if available, otherwise fall back to server state
    const upVoteCollaborators =
        optimisticVote?.upVoteCollaborators ?? serverUpVoteCollaborators;
    const downVoteCollaborators =
        optimisticVote?.downVoteCollaborators ?? serverDownVoteCollaborators;

    // Handle fetcher state changes and optimistic updates
    useEffect(() => {
        if (fetcher.state === 'idle') {
            if (fetcher.data && onSuccessRef.current) {
                // Success: clear optimistic state and call onSuccess
                setOptimisticVote(null);
                onSuccessRef.current(fetcher.data);
            } else if (fetcher.data === null && fetcher.state === 'idle') {
                // Error: rollback optimistic state
                setOptimisticVote(null);
            }
        }
    }, [fetcher.state, fetcher.data]);

    const isVotedUp =
        optimisticVote?.isVotedUp ??
        serverUpVoteCollaborators.some((voter) => voter.id === user.ID);
    const isVotedDown =
        optimisticVote?.isVotedDown ??
        serverDownVoteCollaborators.some((voter) => voter.id === user.ID);

    return {
        reaction: matchedReaction?.reactions,
        upVoteCollaborators,
        downVoteCollaborators,
        isVotedUp,
        isVotedDown,
        handleUpVote,
        handleDownVote,
        handleRevokeVote,
        isVoting: fetcher.state !== 'idle',
    };
};

function getUserVoter(
    user: ReturnType<typeof useUserLoggedIn>['user'],
    collaborators: {
        id: string;
        name: string;
        email: string;
        role: BuildHubRoleEnum;
        image: string | null;
        memberID?: string;
    }[],
    ownerId: number
): ReactionVoter {
    if (user.__typename !== 'Me') {
        throw new Error('User not found');
    }

    const collaborator = collaborators?.find(
        (collaborator) => collaborator.email === user.Email
    );

    if (!collaborator) {
        throw new Error('Collaborator not found');
    }

    return {
        id: Number(collaborator.id),
        name: collaborator.name,
        role: collaborator.role,
        profileImages: collaborator.image,
        isOwner: String(ownerId) === collaborator.memberID,
        lastVotedAt: new Date().toISOString(),
        email: collaborator.email,
    };
}
